import axios from 'axios';
import React, { useState } from 'react'
import { baseURL, tenantId } from '../../config/api';
import usePleaseWait from '../../hooks/usePleaseWait';
import useWebData from '../../hooks/useWebData';
import useConfirm from '../../hooks/useConfirm';

type Props = {
  reservation: any;
  customerId: number;
  setCurrentPage: () => void;
}

const Payments = ({ reservation, setCurrentPage }: Props) => {
  const { webData }: any = useWebData();
  const { setPleasWaitPopup }: any = usePleaseWait();
  const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
  const [creditCard, setCreditCard] = useState<string>("");
  const [cardHolderName, setCardHolderName] = useState<string>("");
  const [expirationDate, setExpirationDate] = useState<string>("");
  const [ccvCode, setCcvCode] = useState<string>("");

  const creditCardValidation = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCreditCard(value);
    const isValid = /^\d{8,19}$/gm.test(value);
    e.target.setCustomValidity(
      isValid ? "" : "Please enetr a valid card number"
    );
  }

  const isValidFormat = (dateStr: string) => {
    const regex = /^(0[1-9]|1[0-2])\/\d{2}$/;
    return regex.test(dateStr);
  };

  const isValidExpirationDate = (dateStr: string) => {
    if (!isValidFormat(dateStr)) {
      return false;
    }

    const [monthStr, yearStr] = dateStr.split('/');
    const month = parseInt(monthStr, 10);
    const year = parseInt(yearStr, 10);

    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear() % 100;

    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      return false;
    }

    return true;
  };

  const expirationDateValidation = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = e.target;

    value = value.replace(/\D/g, '');

    if (value.length > 2) {
      value = value.slice(0, 2) + '/' + value.slice(2);
    }
    setExpirationDate(value);

    e.target.setCustomValidity(
      isValidExpirationDate(value) ? "" : "Invalid expiry date. Please enter a valid MM/YY format with a future date."
    );
  };

  const cvvValidation = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCcvCode(value);
    const isValid = /^\d{3,4}$/gm.test(value);
    e.target.setCustomValidity(
      isValid ? "" : "Please enetr a valid cvv number"
    );
  };


  const handleCreditPayment = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPleasWaitPopup(true);
    await axios
      .post(
        `${baseURL}payment/paymentForCustomer`,
        JSON.stringify({
          creditCardNumber: creditCard,
          expirationDate: expirationDate,
          cardholderName: cardHolderName?.trim(),
          ccvCode: ccvCode,
          cardZipCode: "",
          paymentType: "Payment",
          reservationId: reservation?.reservation
        }),
        {
          headers: {
            'X-Tenant-ID': tenantId,
            "Content-Type": "application/json"
          },
          withCredentials: true,
        }
      )
      .then(() => {       
        setPleasWaitPopup(false);
        setCurrentPage();
      })
      .catch((error) => {
        setConfirmData({
          title: 'Warning',
          summary: error?.response?.data,
          confirmBtn: 'Ok',
          textColor: "!text-red-500 !font-semibold",
          isConfirm: (callback: any) => {
            callback();
          },
        });

        setConfirmPopup(true);
        setPleasWaitPopup(false);
        console.log("Error:", error);
      });
  }


  return (
    <div className='md:px-8'>
      <form onSubmit={(e) => handleCreditPayment(e)}>
        <div className="w-full flex flex-col items-center px-4 py-6 bg-white border-[0.5px] border-gray-300/50 rounded-lg">
          {/* Heading */}
          <div className="w-full flex flex-col md:flex-row items-center justify-between mb-2">
            <p className="text-sm font-bold mb-2">Payment</p>
            <span className="text-sm font-bold text-red-600 mb-2">
              Unpaid bookings will be cancelled
            </span>
          </div>
          <hr className="w-full border-t-2 border-gray-300 mb-4" />

          {/* Credit Card Icons */}
          <div className="w-full flex items-center justify-start gap-4 mb-4">
            <img src="/images/visa-payment.png" alt='visa-payment' className='w-[50px] h-[50px]' />
            <img src="/images/mastercard-payment.png" alt='mastercard-payment' className='w-[60px] h-[50px]' />
            <img src="/images/amex-payment.png" alt='amex-payment' className='w-[50px] h-[50px]' />
            <img src="/images/discover-payment.png" alt='discover-payment' className='w-[50px] h-[50px]' />
          </div>

          {/* Card Form */}
          <div className="w-full border border-gray-300 rounded-tl-md rounded-tr-md overflow-hidden">
            <p className="w-full bg-gray-100 text-sm font-semibold px-4 py-2">
              Authorization Details - <strong>USD</strong>
            </p>
            <hr className="m-0 border-t border-gray-300" />

            <div className="w-full flex flex-col gap-4 px-4 py-4">
              {/* Row 1 */}
              <div className="flex flex-col md:flex-row gap-4 w-full">
                <div className="flex-1">
                  <label className="text-xs font-bold">Credit Card Number</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border rounded-md outline-none text-sm"
                    value={creditCard}
                    onChange={creditCardValidation}
                    required
                  />
                </div>

                <div className="w-full md:w-1/3">
                  <label className="text-xs font-bold">Expiration Date</label>
                  <input
                    type="text"
                    placeholder="MM/YY"
                    className="w-full px-3 py-2 border rounded-md outline-none text-sm"
                    value={expirationDate}
                    onChange={expirationDateValidation}
                    required
                  />
                </div>
              </div>

              {/* Row 2 */}
              <div className="flex flex-col md:flex-row gap-4 w-full">
                <div className="flex-1">
                  <label className="text-xs font-bold">Cardholder Name</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border rounded-md outline-none text-sm"
                    value={cardHolderName}
                    onChange={(e) => setCardHolderName(e.target.value)}
                    required
                  />
                </div>

                <div className="w-full md:w-1/3">
                  <label className="text-xs font-bold">CCV Code</label>
                  <input
                    type="text"
                    placeholder="CVV"
                    className="w-full px-3 py-2 border rounded-md outline-none text-sm"
                    value={ccvCode}
                    onChange={cvvValidation}
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="w-full flex items-center justify-between px-4 py-2 rounded-bl-md rounded-br-md bg-[#ff6926]"
            style={{
              backgroundColor: webData?.websiteHeaderColor
            }}
          >
            <p className="text-sm text-white m-0">Payment</p>
            <p className="text-sm bg-white text-black px-3 py-1 rounded-full m-0">
              ${reservation?.outstandingBalance?.toFixed(2)}
            </p>
          </div>

          <hr className={`w-full !mt-4 bg-[#E4E7EC]`} />
          {/* Submit Button */}
          <button
            type="submit"
            className="
    w-full
    flex
    items-center
    justify-center
    bg-gradient-to-r from-green-500 to-green-600
    hover:from-green-600 hover:to-green-700
    active:scale-[0.98]
    border-2 border-green-600
    shadow-lg
    text-white
    text-base
    font-semibold
    py-3
    mt-6
    rounded-full
    transition-all
    duration-200
  "
          >
            Pay with Credit Card
          </button>

        </div>

      </form>    
    </div>
  )
}

export default Payments